import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FormikHelpers } from 'formik'
import { AxiosError } from 'axios'
import { useUser } from '@matillion/hub-client'
import { Toaster } from '@matillion/component-library'

import {
  useCompleteOAuthV2,
  useCreateOAuthV2,
  useGetProject,
  usePostSecretValueV2
} from 'api/hooks'
import {
  ErrorResponse,
  POSTSecretValueV2Request,
  POSTSetupOAuthV2Response
} from 'api/types'
import Form from 'components/Form'
import { QueryKey } from 'constants/endpoint'
import { OAUTH_STORAGE_ITEM } from 'constants/persistance'
import { AppRoutes, PROJECT_DETAILS_OAUTH } from 'constants/route'
import { StatusCodes } from 'constants/statusCodes'
import CreateOAuth from 'modules/FormsContent/CreateOAuth'
import { JSONValue, SecretReferenceTypes } from 'types'

import {
  CreateOAuthFormikValueTypes,
  initialValues,
  splitValuesSaveToSecretAndSendToAuthServer
} from '.'
import { useCreateOAuthContext } from 'modules/Projects/CreateOAuth/CreateOAuthContext'
import { createOAuthMutationDataV2 } from 'api/mutation/createOAuthMutationV2'

const CreateOAuthForm: FunctionComponent = () => {
  const { clearToasts, makeToast } = Toaster.useToaster()
  const [authName, setAuthName] = useState<string>('')
  const [agentId, setAgentId] = useState<string>('')
  const [additionalOAuthCompleteSecrets, setAdditionalOAuthCompleteSecrets] =
    useState<{
      [key: string]: JSONValue
    }>()
  const [currentAttemptId, setCurrentAttemptId] = useState<string>()
  const [isWaiting, setIsWaiting] = useState<boolean>(false)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const navigate = useNavigate()
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { organisation } = useUser()
  const { validationSchema, selectedOAuth } = useCreateOAuthContext()
  const { data: projectData } = useGetProject(projectId!)
  const { mutateAsync: createSecretValue } = usePostSecretValueV2(projectId)
  const { mutateAsync: createOAuth } = useCreateOAuthV2(projectId!)
  const { mutateAsync: wrapupOAuth } = useCompleteOAuthV2(projectId!)

  const navigateToOAuthConnections = useCallback(() => {
    queryClient.invalidateQueries([
      QueryKey.SECRET_REFERENCES,
      projectId!,
      [
        SecretReferenceTypes.OAUTH_USER_TOKENS,
        SecretReferenceTypes.OAUTH_CLIENT_CRED,
        SecretReferenceTypes.COMPOSITE_TOKEN
      ]
    ])
    window.sessionStorage.removeItem(OAUTH_STORAGE_ITEM)
    navigate(AppRoutes.getProjectDetails(projectId!, PROJECT_DETAILS_OAUTH))
  }, [navigate, projectId, queryClient])

  const submitForm = (
    values: CreateOAuthFormikValueTypes,
    { setSubmitting }: FormikHelpers<CreateOAuthFormikValueTypes>
  ) => {
    clearToasts()
    const { provider, name, environmentAgent, authType, ...jsonBlob } = values
    const authServerParamsAndSecretParams =
      splitValuesSaveToSecretAndSendToAuthServer(
        selectedOAuth!,
        authType.id,
        jsonBlob
      )
    if (values.authType?.id === SecretReferenceTypes.OAUTH_USER_TOKENS) {
      setAdditionalOAuthCompleteSecrets(
        authServerParamsAndSecretParams.saveToSecret
      )
      submitFormOauthToken(
        values,
        authServerParamsAndSecretParams.sendToAuthServer,
        setSubmitting
      )
    } else {
      const secretValueV2Request: POSTSecretValueV2Request = {
        name: name,
        type: authType.id as SecretReferenceTypes,
        secretName: name,
        secretValue: authServerParamsAndSecretParams.saveToSecret,
        agentId: environmentAgent.id,
        locationId: projectData!.secretLocationIds[0],
        metadata: {
          provider: provider.id
        }
      }
      submitFormClientCred(secretValueV2Request, setSubmitting)
    }
  }

  const submitFormClientCred = (
    values: POSTSecretValueV2Request,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    setSubmitting(true)

    createSecretValue({ values })
      .then((_response) => {
        makeToast({
          title: t('secrets.createOAuth.wrapup.responseMessage.success', {
            oauthName: values.name
          }),
          message: '',
          type: 'success'
        })
        navigateToOAuthConnections()
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const submitFormOauthToken = (
    values: CreateOAuthFormikValueTypes,
    providerParameters: { [key: string]: string },
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    clearToasts()
    setSubmitting(true)
    setIsWaiting(true)

    const urlWithoutSubdomain =
      `${window.location.origin}/oauth-redirect`.replace(
        `${organisation.subdomain}.`,
        ''
      )

    createOAuth({
      values: createOAuthMutationDataV2({
        ...values,
        providerParameters,
        callbackUrl: urlWithoutSubdomain,
        accountId: organisation.subdomain
      })
    })
      .then(async (res: POSTSetupOAuthV2Response) => {
        const redirectUrl = res.redirectUrl
        const redirectUrlObject = new URL(redirectUrl)

        const { state: stateString }: Record<string, string> = [
          ...redirectUrlObject.searchParams.entries()
        ].reduce(
          (previous, [key, value]) => ({
            ...previous,
            [key]: value
          }),
          {}
        )

        const stateId = stateString.split('_')[0]

        setCurrentAttemptId(stateId)
        setAuthName(values.name)
        setAgentId(values.environmentAgent.id)
        setOpenModal(true)

        window.open(res.redirectUrl)

        await queryClient.invalidateQueries([QueryKey.CREATE_OAUTH, projectId])
      })
      .catch((err: AxiosError) => {
        const error = err?.response?.data as ErrorResponse
        const errorMessage = error?.detail
          ? error.detail
          : t('secrets.createOAuth.responseMessage.conflictName')
        setIsWaiting(false)
        makeToast({
          title:
            error.status === StatusCodes.CONFLICT
              ? errorMessage
              : t('secrets.createOAuth.responseMessage.error'),
          message: '',
          type: 'error'
        })
      })
      .finally(() => setSubmitting(false))
  }

  const handleMessage = useCallback(
    (event: MessageEvent<Record<string, string>>) => {
      if (
        event.data?.type ===
        'matillion.project-explorer-frontend.oauth.redirect'
      ) {
        const { attemptId, code, state } = event.data

        if (attemptId !== currentAttemptId || !isWaiting) {
          return undefined
        }

        setOpenModal(false)
        wrapupOAuth({
          values: {
            code,
            state,
            name: authName,
            agentId: agentId,
            secretJson: additionalOAuthCompleteSecrets
          }
        })
          .then(async () => {
            await queryClient.invalidateQueries([QueryKey.COMPLETE_OAUTH])
            makeToast({
              title: t('secrets.createOAuth.wrapup.responseMessage.success', {
                oauthName: authName
              }),
              message: '',
              type: 'success'
            })
            navigateToOAuthConnections()
          })
          .catch((_err: ErrorResponse) => {
            makeToast({
              title: t('secrets.createOAuth.wrapup.responseMessage.error'),
              message: '',
              type: 'error'
            })
          })
          .finally(() => setIsWaiting(false))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      authName,
      currentAttemptId,
      isWaiting,
      wrapupOAuth,
      agentId,
      additionalOAuthCompleteSecrets
    ]
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)

    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage])

  const handleUserAbandonOauth = () => {
    setOpenModal(false)
    navigateToOAuthConnections()
  }

  return (
    <Form<CreateOAuthFormikValueTypes>
      formikValues={{
        onSubmit: submitForm,
        initialValues,
        validateOnMount: true,
        enableReinitialize: true,
        validationSchema,
        initialTouched: false
      }}
      translationPrefix="secrets.createOAuth"
      stepInfo={{
        isLastStep: true,
        stepIndex: 0
      }}
      persistingStorageId={OAUTH_STORAGE_ITEM}
    >
      <CreateOAuth
        onFormCancel={navigateToOAuthConnections}
        onAwaitingCancel={handleUserAbandonOauth}
        showAwaitingModal={openModal}
      />
    </Form>
  )
}

export default CreateOAuthForm
